import {CalendarIcon as CalendarDays} from '../icons/AppIcons';
import {eventTagLabel} from "../../utils/eventSetup";
import React, {useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {ChevronDown} from 'lucide-react';
import {onAuthStateChanged} from "firebase/auth";
import {doc, getDoc} from "firebase/firestore";
import {auth, db} from "../../Security/firebase-config";
import {subscribeCalendarEvents} from "./calendarSubscriptions";
import {buildNextEvents, eventDate, eventParticipants, eventSummary, eventTagColors, eventTime, participantRoleColor} from "./nextEventModel";
import "./NextCalendarEvent.css";

export function NextEventDetails({event, team, directory}) {
  const color = eventTagColors[event.tag] || eventTagColors.Other;
  const participants = eventParticipants(event, team, directory);
  const maps = Array.isArray(event.mapSlots) && event.mapSlots.length ? event.mapSlots.map((slot, index) => `Map ${index + 1}: ${slot.mapName || slot.mapId || "Unknown"}`).join(" · ") : event.mapName || event.mapId || "Unknown";
  return <div className="NxtCaEvnt-details">
    {(event.note || event.competition) && <p className="NxtCaEvnt-event-title">{event.note || event.competition}</p>}
    <div className="NxtCaEvnt-row"><span className="NxtCaEvnt-lbl">Starts:</span><span>{eventDate(event.startUTC)} at <span style={{color}}>{eventTime(event.startUTC)}</span></span></div>
    <div className="NxtCaEvnt-row"><span className="NxtCaEvnt-lbl">Ends:</span><span>{eventDate(event.endUTC)} at <span style={{color}}>{eventTime(event.endUTC)}</span></span></div>
    <div className="NxtCaEvnt-row"><span className="NxtCaEvnt-lbl">Tag:</span><span style={{color}}>{eventTagLabel(event)}</span></div>
    <div className="NxtCaEvnt-row"><span className="NxtCaEvnt-lbl">Source:</span><span>{event.sourceName}</span></div>
    <div className="NxtCaEvnt-row"><span className="NxtCaEvnt-lbl">Format:</span><span>{/^bo[1357]$/i.test(event.matchFormat || "") ? `Best of ${event.matchFormat.slice(2)}` : "Not specified"}</span></div>
    <div className="NxtCaEvnt-row"><span className="NxtCaEvnt-lbl">Map played:</span><span>{maps}</span></div>
    <div className="NxtCaEvnt-row"><span className="NxtCaEvnt-lbl">Participants:</span><span className="NxtCaEvnt-participants">
      {participants.length ? participants.map((person, index) => <span className="NxtCaEvnt-pill" key={person.uid || `${person.username}-${index}`} style={{color: participantRoleColor(person.role)}} title={person.role}>{person.username}</span>) : "No participants added"}
    </span></div>
  </div>;
}

export default function NextCalendarEvent() {
  const [data, setData] = useState(null), [viewer, setViewer] = useState(null), [checked, setChecked] = useState(false);
  const [directory, setDirectory] = useState({}), [now, setNow] = useState(Date.now), [open, setOpen] = useState(false);
  const [position, setPosition] = useState({top: 0, left: 8, maxHeight: 400});
  const anchor = useRef(null), panel = useRef(null), closeTimer = useRef(null);
  const panelId = useId();
  useEffect(() => {
    let stopCalendar = () => {}, generation = 0, alive = true;
    const names = new Set();
    const stopAuth = onAuthStateChanged(auth, user => {
      const current = ++generation;
      stopCalendar(); names.clear(); setData(null); setDirectory({}); setViewer(user ? {uid: user.uid} : null); setChecked(!user); setOpen(false);
      if (!user) return;
      stopCalendar = subscribeCalendarEvents({db, userId: user.uid,
        onChange: snapshot => {
          if (!alive || current !== generation) return;
          setData(snapshot); setViewer({uid: user.uid, username: snapshot.user?.username || user.displayName || ""}); setChecked(true);
          const ids = new Set(Object.values(snapshot.teams || {}).flatMap(team => [
            ...(Array.isArray(team?.members) ? team.members : []).map(member => typeof member === "string" ? member : member?.uid), ...Object.keys(team?.teamRoles || {}),
          ]).filter(Boolean));
          ids.forEach(uid => {
            if (names.has(uid)) return;
            names.add(uid);
            getDoc(doc(db, "uids", uid)).then(result => {
              if (alive && current === generation && result.data()?.username) setDirectory(previous => ({...previous, [uid]: result.data().username}));
            }).catch(() => {});
          });
        }, onError: () => {if (alive && current === generation) setChecked(true);},
      });
    });
    const clock = setInterval(() => setNow(Date.now()), 30000);
    return () => {alive = false; stopCalendar(); stopAuth(); clearInterval(clock); clearTimeout(closeTimer.current);};
  }, []);
  const events = useMemo(() => buildNextEvents(data, viewer || {}, now), [data, viewer, now]);
  const next = events[0];
  const show = () => {clearTimeout(closeTimer.current); setOpen(true);};
  const hideSoon = () => {clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 180);};
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(620, window.innerWidth - 16), top = rect.bottom;
      setPosition({top, left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)), maxHeight: Math.max(80, window.innerHeight - top - 20)});
    };
    place(); window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    const outside = e => {if (!anchor.current?.contains(e.target) && !panel.current?.contains(e.target)) setOpen(false);};
    const escape = e => {if (e.key === "Escape") {anchor.current?.focus(); setOpen(false);}};
    document.addEventListener("pointerdown", outside); document.addEventListener("keydown", escape);
    return () => {window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape);};
  }, [open]);
  return <div className="NxtCaEvnt-root">
    <button ref={anchor} className="NxtCaEvnt-chip" type="button" aria-expanded={open} aria-controls={panelId} onMouseEnter={show} onMouseMove={show} onMouseLeave={hideSoon} onClick={show} onFocus={show} onBlur={e => {if (!panel.current?.contains(e.relatedTarget)) hideSoon();}}>
      <CalendarDays size={17} aria-hidden="true"/>
      <span className="NxtCaEvnt-summary">{next ? eventSummary(next) : !checked ? "Loading events…" : !viewer ? "Login to see events" : "No upcoming events"}</span>
      {next && <span className="NxtCaEvnt-time" style={{color: eventTagColors[next.tag] || eventTagColors.Other}}>{eventTime(next.startUTC)}</span>}
    </button>
    {open && createPortal(<div ref={panel} className="NxtCaEvnt-hover-area" style={{top:position.top,left:position.left}}
      onMouseEnter={show} onMouseLeave={hideSoon} onFocus={show} onBlur={e => {if (!panel.current?.contains(e.relatedTarget) && !anchor.current?.contains(e.relatedTarget)) hideSoon();}}>
      <div id={panelId} className="NxtCaEvnt-panel" role="region" aria-label="Upcoming event details" style={{maxHeight:position.maxHeight}}>
      <div className="NxtCaEvnt-head">Next Event</div>
      {next ? <NextEventDetails event={next} team={data?.teams?.[next.__teamId]} directory={directory}/> : <p>{!checked ? "Loading events…" : !viewer ? "Please login to use the calendar." : "No upcoming events."}</p>}
      {next && <div className="NxtCaEvnt-following"><div className="NxtCaEvnt-head">Following events</div>
        {events.slice(1).map(event => <details key={event.key} className="NxtCaEvnt-following-event">
          <summary title={eventSummary(event)}><span>{eventSummary(event)}</span><ChevronDown size={15} aria-hidden="true"/></summary>
          <NextEventDetails event={event} team={data?.teams?.[event.__teamId]} directory={directory}/>
        </details>)}
        {events.length === 1 && <p>No more upcoming events in the next 60 days.</p>}
      </div>}
      </div>
    </div>, document.body)}
  </div>;
}
